import React from 'react';
import PropTypes from 'prop-types';

import {MapContainer, TileLayer, Marker, Popup, useMapEvent, useMap} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {Stack, Typography} from 'views/components';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Create custom marker icon with profile image
const createProfileIcon = (profileImg) => {
  if (!profileImg) {
    return new L.Icon.Default();
  }

  return L.divIcon({
    html: `
      <div style="
        position: relative;
        width: 46px;
        height: 56px;
      ">
        <div style="
          position: absolute;
          top: 0;
          left: 0;
          width: 46px;
          height: 46px;
          border-radius: 50%;
          border: 3px solid #1976d2;
          background: white url('${profileImg}') center/cover;
        "></div>
        <div style="
          position: absolute;
          top: 46px;
          left: 17px;
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 10px solid #1976d2;
        "></div>
      </div>
    `,
    className: 'custom-profile-marker',
    iconAnchor: [23, 56],
    popupAnchor: [0, -56],
  });
};

const CaregiverMap = (props) => {
  const {coordinates, onLocationClick, onBoundsChange, caregivers, onMarkerClick, shouldRecenter, showCenterMarker, zoom = 6} = props;

  // Scale control component for Leaflet
  const MapScaleControl = React.useCallback(() => {
    const map = useMap();
    React.useEffect(() => {
      const scale = L.control.scale({position: 'bottomleft', metric: true, imperial: false});
      scale.addTo(map);
      return () => {
        scale.remove();
      };
    }, [map]);
    return null;
  }, []);

  // EventBridge - Internal component that bridges Leaflet map events to React callbacks
  const EventBridge = React.useCallback(() => {
    const map = useMap();

    // Forward click events to the parent callback
    useMapEvent('click', (e) => onLocationClick?.(e.latlng));

    const sendBoundsAndZoom = () => {
      const bounds = map.getBounds();
      const zoom = map.getZoom();
      onBoundsChange?.({bounds, zoom});
    };

    // Send initial bounds/zoom once when the map is ready
    React.useEffect(() => {
      if (!map) return;
      sendBoundsAndZoom();
    }, [map, onBoundsChange]);

    useMapEvent('moveend', sendBoundsAndZoom);
    useMapEvent('zoomend', sendBoundsAndZoom);
    return null;
  }, []);


  // Recenter map only when explicitly triggered
  const RecenterOnCoords = React.useCallback(({coords, shouldRecenter}) => {
    const map = useMap();
    React.useEffect(() => {
      if (coords?.length === 2 && shouldRecenter) {
        map.setView(coords);
      }
    }, [coords, map, shouldRecenter]);
    return null;
  }, []);

  return (
    <MapContainer center={coordinates} zoom={zoom} minZoom={4} style={{height: '100%', width: '100%'}}>
      <MapScaleControl />
      <EventBridge />
      <RecenterOnCoords coords={coordinates} shouldRecenter={shouldRecenter} />

      {/* OpenStreetMap tile layer */}
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      />

      {/* Center marker - only visible when user has selected a location */}
      {
        showCenterMarker &&
          <Marker position={coordinates}>
            <Popup autoPan={false}>
              <Typography variant="body2">Selected Location</Typography>
            </Popup>
          </Marker>
      }

      {/* Caregiver location markers */}
      {
        caregivers?.map((caregiver) => (
          <Marker
            key={caregiver.id}
            position={[caregiver.location.lat, caregiver.location.lng]}
            icon={createProfileIcon(caregiver.profileImg)}
            eventHandlers={{
              popupopen: () => onMarkerClick?.(caregiver.id),
              popupclose: () => onMarkerClick?.(null),
            }}
          >
            <Popup autoPan={false}>
              <Stack >
                <Typography variant="subtitle2" sx={{fontWeight: 700}}>{caregiver.name}</Typography>
              </Stack>
            </Popup>
          </Marker>
        ))
      }
    </MapContainer>
  );
};

CaregiverMap.propTypes = {
  coordinates: PropTypes.array.isRequired,
  onLocationClick: PropTypes.func,
  onBoundsChange: PropTypes.func,
  onCenterChange: PropTypes.func,
  shouldRecenter: PropTypes.bool,
  showCenterMarker: PropTypes.bool,
  caregivers: PropTypes.array,
  center: PropTypes.array,
  onMarkerClick: PropTypes.func,
  zoom: PropTypes.number,
};

export default CaregiverMap;
